/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import PortalModal from '../../components/shared/PortalModal';

const ReassignModal = ({ open, onClose, onSubmit, booking, userRole, userId, isDark }) => {
  const [barbers, setBarbers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    barber_id: '',
    service_id: ''
  });

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open && booking) {
      setFormData({
        barber_id: booking.barber.id,
        service_id: booking.service.id
      });
      setError('');
      loadData();
    } else {
      setBarbers([]);
      setServices([]);
      setError('');
      setFormData({ barber_id: '', service_id: '' });
    }
  }, [open, booking]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load barbers and services in parallel
      const [barbersResponse, servicesResponse] = await Promise.all([
        axiosClient.get('/admin/barbers?status=active&pageSize=100'),
        axiosClient.get('/admin/services?status=active&pageSize=100')
      ]);

      let availableBarbers = barbersResponse.data.items || [];
      
      // For barbers, only show self if userRole is 'barber'
      if (userRole === 'barber') {
        availableBarbers = availableBarbers.filter(barber => barber.id === userId);
      }

      setBarbers(availableBarbers);
      setServices(servicesResponse.data.items || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load barbers and services');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const getSelectedBarber = () => {
    return barbers.find(b => b.id === parseInt(formData.barber_id));
  };

  const getSelectedService = () => {
    return services.find(s => s.id === parseInt(formData.service_id));
  };

  const hasChanges = () => {
    return (
      parseInt(formData.barber_id) !== booking.barber.id ||
      parseInt(formData.service_id) !== booking.service.id
    );
  };

  const getServiceDurationChange = () => {
    const currentService = booking.service;
    const newService = getSelectedService();
    
    if (!newService || newService.id === currentService.id) {
      return null;
    }
    
    const change = newService.duration_min - currentService.duration_min;
    return { change, newDuration: newService.duration_min, oldDuration: currentService.duration_min };
  };

  const handleSubmit = async () => {
    if (!hasChanges()) {
      setError('No changes to save');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const payload = {};
      
      // Only include changed fields
      if (parseInt(formData.barber_id) !== booking.barber.id) {
        payload.barber_id = parseInt(formData.barber_id);
      }
      
      if (parseInt(formData.service_id) !== booking.service.id) {
        payload.service_id = parseInt(formData.service_id);
      }

      await onSubmit(payload);
      
      // Success - parent will close modal
    } catch (error) {
      console.error('Error reassigning booking:', error);
      
      // Handle specific server errors
      if (error.response?.data?.error) {
        const errorType = error.response.data.error;
        const errorMessage = error.response.data.message;
        
        switch (errorType) {
          case 'BOOKING_CONFLICT':
            setError('This change conflicts with another booking. Consider rescheduling to a different time.');
            break;
          case 'BARBER_NOT_FOUND':
            setError('Selected barber is not available');
            break;
          case 'BARBER_NOT_IN_SHOP':
            setError('Selected barber is not in your shop');
            break;
          case 'SERVICE_NOT_FOUND':
            setError('Selected service is not available');
            break;
          case 'FORBIDDEN':
            setError('You do not have permission to make this change');
            break;
          default:
            setError(errorMessage || 'Failed to update booking. If the service duration changed, you may need to reschedule to a different time.');
        }
      } else {
        setError('Failed to update booking');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  if (!open) return null;

  const durationChange = getServiceDurationChange();

  return (
    <PortalModal open={open}>
      <div className="hc-modal-overlay bg-black bg-opacity-50 flex items-start sm:items-center justify-center">
        <div className={`hc-modal-panel p-4 md:p-6 rounded-lg max-w-md w-full my-8 ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Change Barber/Service
          </h3>
          <button
            onClick={handleClose}
            disabled={submitting}
            className={`text-gray-400 hover:text-gray-600 ${submitting ? 'opacity-50' : ''}`}
          >
            ✕
          </button>
        </div>

        {/* Current Booking Info */}
        <div className={`p-3 rounded-lg mb-4 text-sm ${
          isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-600'
        }`}>
          <p><strong>Client:</strong> {booking?.client?.name}</p>
          <p><strong>Time:</strong> {booking?.start_local ? 
            new Date(booking.start_local).toLocaleString('en-US', {
              weekday: 'short',
              month: 'short', 
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            }) : 'N/A'
          }</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Barber Selection */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Barber
              </label>
              <select
                value={formData.barber_id}
                onChange={(e) => handleInputChange('barber_id', e.target.value)}
                disabled={userRole === 'barber' || submitting}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } ${(userRole === 'barber' || submitting) ? 'opacity-50' : ''}`}
              >
                <option value="">Select a barber</option>
                {barbers.map(barber => (
                  <option key={barber.id} value={barber.id}>
                    {barber.name}
                  </option>
                ))}
              </select>
              {userRole === 'barber' && (
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Barbers can only assign to themselves
                </p>
              )}
            </div>

            {/* Service Selection */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Service
              </label>
              <select
                value={formData.service_id}
                onChange={(e) => handleInputChange('service_id', e.target.value)}
                disabled={submitting}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } ${submitting ? 'opacity-50' : ''}`}
              >
                <option value="">Select a service</option>
                {services.map(service => (
                  <option key={service.id} value={service.id}>
                    {service.name} ({service.duration_min} min)
                  </option>
                ))}
              </select>
            </div>

            {/* Duration Change Info */}
            {durationChange && (
              <div className={`p-3 rounded-lg text-sm ${
                isDark ? 'bg-blue-900 text-blue-200' : 'bg-blue-50 text-blue-800'
              }`}>
                <p className="font-medium">⚠️ Duration Change</p>
                <p>
                  Duration will change from {durationChange.oldDuration} min to {durationChange.newDuration} min 
                  ({durationChange.change > 0 ? '+' : ''}{durationChange.change} min).
                </p>
                <p className="mt-1">
                  If this causes conflicts, you may need to reschedule to a different time.
                </p>
              </div>
            )}

            {/* General Info Note */}
            <div className={`p-3 rounded-lg text-sm ${
              isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-600'
            }`}>
              <p>💡 Duration may change if service changes.</p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                {error}
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-end space-x-2 pt-4">
              <button
                onClick={handleClose}
                disabled={submitting}
                className={`px-4 py-2 rounded ${
                  isDark 
                    ? 'bg-gray-600 hover:bg-gray-700 text-white' 
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                } ${submitting ? 'opacity-50' : ''}`}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!hasChanges() || submitting || !formData.barber_id || !formData.service_id}
                className={`px-4 py-2 rounded text-white ${
                  !hasChanges() || submitting || !formData.barber_id || !formData.service_id
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {submitting ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </span>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        )}
        </div>
      </div>
    </PortalModal>
  );
};

export default ReassignModal;
